class LookupDictionary<T> {
    private getKey: (item: T) => string;
    private keys: string[];
    private values: { [key: string]: T[] };

    constructor(getKey: (item: T) => string, _values: T[] = []) {
        _values ??= [];

        if (!getKey) {
            console.error("LookupDictionary requires a get fct");
            throw null;
        }

        this.values = {};
        for (const o of _values) {
            let key = getKey(o);
            this.values[key] = this.values[key] || [];
            this.values[key].push(o);
        }

        this.keys = Object.keys(this.values);
        this.getKey = getKey;
    }

    public get(key: string | number): T[] {
        if (typeof key == "string")
            return this.values[key];

        return this.values[key.toString()];
    }

    public forEach(mutation: (items: T[], key: string) => void): void {
        this.keys.forEach(key => mutation(this.values[key], key));
    }

    public map<T1>(predicate: (items: T[], key: string) => T1): T1[] {
        return this.keys.map(key => predicate(this.values[key], key));
    }

    public filter(predicate: (item: T, key: string) => boolean): LookupDictionary<T> {
        let newValues = [];
        this.keys.forEach(key => {
            newValues = newValues.concat(this.values[key].filter(value => predicate(value, key)));
        });

        return newValues.toLookupDictionary(x => this.getKey(x));
    }

    public filterGroup(predicate: (items: T[], key: string) => boolean): LookupDictionary<T> {
        let keysFiltered = this.keys.filter(key => predicate(this.values[key], key));

        let newValues = [];
        keysFiltered.forEach(key => newValues = newValues.concat(this.values[key]));

        return newValues.toLookupDictionary(x => this.getKey(x));
    }

    public updateAll(mutation: (items: T[], key: string) => T[]): void {
        this.keys.forEach(key => this.values[key] = mutation(this.values[key], key));
    }

    public updateAllElement(mutation: (item: T, key: string) => void): void {
        this.updateAll((items, key) => {
            items.forEach(item => mutation(item, key));
            return items;
        });
    }

    public updateElement(key: string, mutation: (items: T[], key: string) => T[]): void {
        this.updateAll((_items, _key) => {
            if (key == _key)
                return mutation(_items, _key);

            return _items;
        });
    }

    public toArray(): T[] {
        let newValues: T[] = [];
        this.forEach(x => x.forEach(y => newValues.push(y)));
        return newValues;
    }

    public toArrayOfArray(): T[][] {
        let newGroups: T[][] = [];
        this.forEach(items => newGroups.push(items));
        return newGroups;
    }
}

export { LookupDictionary };
